import { ReactNode } from "react";
import { usePermissions } from "../../hooks/usePermissions";
import { ModulePermissions } from "../../lib/permissions";

type Module = keyof ModulePermissions;
type PermissionGuardProps = {
  module: Module;
  action?:
    | "view"
    | "create"
    | "edit"
    | "delete"
    | "approve"
    | "sync"
    | "updateStatus";
  children: ReactNode;
  fallback?: ReactNode;
};

/**
 * Component that conditionally renders children based on user permissions
 * @param module - The module/feature to check permissions for
 * @param action - The specific action to check (defaults to 'view')
 * @param children - Content to render if user has permission
 * @param fallback - Optional content to render if user lacks permission
 */
export const PermissionGuard = ({
  module,
  action = "view",
  children,
  fallback = null,
}: PermissionGuardProps) => {
  const {
    canView,
    canCreate,
    canEdit,
    canDelete,
    canApprove,
    canSync,
    canUpdateStatus,
  } = usePermissions();

  const hasPermission = () => {
    switch (action) {
      case "view":
        return canView(module);
      case "create":
        return canCreate(module);
      case "edit":
        return canEdit(module);
      case "delete":
        return canDelete(module);
      case "approve":
        return canApprove(module);
      case "sync":
        return canSync(module);
      case "updateStatus":
        return canUpdateStatus(module);
      default:
        return false;
    }
  };

  return hasPermission() ? <>{children}</> : <>{fallback}</>;
};
